import { Modal } from 'bootstrap'
import { forkJoin, of, Subscription } from 'rxjs'
import { distinctUntilKeyChanged, filter, map, startWith, switchMap } from 'rxjs/operators'

import { AfterContentInit, Component, Input, OnDestroy } from '@angular/core'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { Feature, FeatureCustomField, FeatureTypes } from '@classes/Feature'
import { FeatureProperty } from '@classes/FeatureProperty'
import { FeatureService } from '@services/feature.service'
import { FileReferenceService } from '@services/file-reference.service'
import { ProjectService } from '@services/project.service'
import { RightSidebarService } from '@services/right-sidebar.service'
import { SceneService } from '@services/scene.service'

interface FeatureTypeConfig {
    label: string
    icon: string
    gradientFrom: string
    gradientTo: string
}

const FEATURE_TYPE_CONFIG: Record<FeatureTypes, FeatureTypeConfig> = {
    '3D': { label: '3D Model', icon: 'fas fa-cube', gradientFrom: '#e67e22', gradientTo: '#f39c12' },
    'cutOut': { label: 'Cut Out', icon: 'fas fa-scissors', gradientFrom: '#8e44ad', gradientTo: '#9b59b6' },
    'group': { label: 'Group', icon: 'fas fa-list', gradientFrom: '#27ae60', gradientTo: '#2ecc71' },
    'icon': { label: 'Icon', icon: 'fa-solid fa-tag', gradientFrom: '#2980b9', gradientTo: '#3498db' },
    'image': { label: 'Image', icon: 'far fa-image', gradientFrom: '#8e44ad', gradientTo: '#9b59b6' },
    'label': { label: 'Text Label', icon: 'fas fa-font', gradientFrom: '#16a085', gradientTo: '#1abc9c' },
    'line': { label: 'Line', icon: 'fa-solid fa-minus', gradientFrom: '#2c3e50', gradientTo: '#34495e' },
    'marker': { label: 'Marker', icon: 'fas fa-map-marker-alt', gradientFrom: '#c0392b', gradientTo: '#e74c3c' },
    'polygon': { label: 'Polygon', icon: 'fa-solid fa-draw-polygon', gradientFrom: '#27ae60', gradientTo: '#2ecc71' },
    'rasterImage': { label: 'Raster Layer', icon: 'fa-solid fa-layer-group', gradientFrom: '#d35400', gradientTo: '#e67e22' },
    'view': { label: '360° View', icon: 'fas fa-street-view', gradientFrom: '#e74c3c', gradientTo: '#c0392b' },
}

@Component({
    selector: 'shared-view-feature-details',
    templateUrl: './view-feature-details.component.html',
    styleUrls: ['./view-feature-details.component.css'],
    standalone: false
})
export class ViewFeatureDetailsComponent implements AfterContentInit, OnDestroy {
    @Input() detailsType: 'panel' | 'modal' = 'modal'
    @Input() modalSize: 'medium' | 'large' | 'fullscreen' = 'medium'

    public modal: Modal

    private _subscriptions: Subscription[] = []

    public feature: Feature
    public sceneName: string = ''
    public attachments: FeatureProperty[] = []
    public customFields: FeatureCustomField[] = []
    public isDescriptionSet: boolean = false
    public retrievingCustomFields: boolean = false
    public sanitizedMessage: SafeHtml

    get hasAttachments() { return this.attachments?.length > 0 }
    get hasCustomFields() { return this.customFields?.length > 0 }
    get hasDescription() { return this.isDescriptionSet }
    get allEmpty() { return !this.hasDescription && !this.hasCustomFields && !this.hasAttachments && !this.retrievingCustomFields }

    get typeConfig(): FeatureTypeConfig {
        return FEATURE_TYPE_CONFIG[this.feature?.type] ?? FEATURE_TYPE_CONFIG['marker']
    }

    get featureLastChanged(): string {
        if (!this.feature?.lastChanged) return ''
        return new Date(this.feature.lastChanged).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    }

    get heroGradient(): string {
        const config = this.typeConfig
        return `linear-gradient(135deg, ${config.gradientFrom} 0%, ${config.gradientTo} 50%, ${config.gradientFrom}88 100%)`
    }

    constructor(
        private _domSanitizer: DomSanitizer,
        private _featureService: FeatureService,
        private _fileReferenceService: FileReferenceService,
        private _projectService: ProjectService,
        private _rightSidebarService: RightSidebarService,
        private _sceneService: SceneService,
    ) { }

    ngAfterContentInit(): void {
        this._subscriptions.push(
            this._featureService.selectedFeature$.pipe(
                startWith(this._featureService.selectedFeature),
                filter(feature => feature != undefined),
                distinctUntilKeyChanged('id'),
                filter(feature => feature.interactions.some(i => i.actions.some(a => a.key == 'showDetails'))),
                map(feature => new Feature(feature.sceneID, feature.name, feature.type, feature)),
                switchMap(feature => {
                    this.clearState()

                    if (!this._featureService.retrievedDetails.has(feature.id) && feature.origin !== "external") {  // The feature.origin -> external is connections
                        this.retrievingCustomFields = true

                        return this._featureService.getFeaturesDetails(feature).pipe(
                            map(details => {
                                this.retrievingCustomFields = false

                                feature.description = details.description
                                feature.customFields = details.customFields

                                return feature
                            })
                        )
                    } else {
                        return of(feature)
                    }
                }),
            ).subscribe(feature => {
                this.feature = feature
                this.sceneName = this._sceneService.selectedScene?.name ?? ''

                let description = feature.description

                /** Set values of description, properties, and attachments */
                this.setDescription(description)
                this.customFields = feature?.customFields
                this._featureService.updateFeaturesLocally(feature)

                this.attachments = feature?.attachments
                    .filter(attachment => {
                        if (attachment.value == null || attachment.value == '') { //
                            return false
                        } else if (attachment.isStoredFile) { // Regular Attachments (EFS or S3)
                            return true
                        } else { // Remote Attachments
                            try {
                                const parsedValue = JSON.parse(attachment.value)
                                const url = parsedValue?.url

                                return url != null && url != ''
                            } catch (e) {
                                return false
                            }
                        }
                    })
                    .sort((a, b) => {
                        const getValueToSortBy = (prop: FeatureProperty) => {
                            if (prop.isStoredFile) {
                                return (prop.s3ObjectReference?.filename ?? prop.value)?.toString().toLowerCase() || ''
                            } else {
                                try {
                                    const { displayName } = JSON.parse(prop.value)

                                    return displayName.toString().toLowerCase()
                                } catch (e) {
                                    return ''
                                }
                            }
                        }
                        let aValue: string = getValueToSortBy(a)
                        let bValue: string = getValueToSortBy(b)

                        if (aValue > bValue) return 1
                        else if (aValue < bValue) return -1
                        else return 0
                    })

                if (!this.hasAttachments) this._featureService.retrievedDetails.add(feature.id)

                /** Show image attachments inline in the description. */
                const embeddedImageSources = this.attachments
                    .filter(attachment => !attachment.isStoredFile || attachment.type == 'asset' || this.isStoredImageAttachment(attachment))
                    .map(attachment => {
                        if (this.isStoredImageAttachment(attachment) && attachment.type != 'asset') {
                            if (attachment.s3ObjectReferenceId != null) {
                                return this._fileReferenceService.blobFromS3Reference(attachment.s3ObjectReferenceId).pipe(
                                    map(blob => `<p></p><img src="${blob}"></img>`)
                                )
                            }

                            if (attachment.fileReference != null) {
                                const publicProject = this._projectService.currentProject?.publicStatus
                                const fileOwnerID = this._projectService.currentProject.id
                                const fileContext = publicProject ? 'public' : undefined

                                return this._fileReferenceService.getUrlForFileReference(attachment.fileReference, fileOwnerID, fileContext).pipe(
                                    switchMap(url => this._fileReferenceService.createBlob(url).pipe(
                                        map(blob => `<p></p><img src="${blob}"></img>`)
                                    ))
                                )
                            }
                        }

                        if (attachment.type == 'asset') {
                            const { showInDetails, assetID } = JSON.parse(attachment.value)

                            if (showInDetails) {
                                // Breadcrumb: Backend getFeatureDetailsFromAssetData() returns these for feature assetID links.
                                if (attachment.s3ObjectReferenceId != null) {
                                    return this._fileReferenceService.blobFromS3Reference(attachment.s3ObjectReferenceId).pipe(
                                        map(blob => `<p></p><img src="${blob}"></img>`)
                                    )
                                }

                                if (attachment.fileReference != null) {
                                    const publicProject = this._projectService.currentProject?.publicStatus
                                    const fileOwnerID = publicProject ? this._projectService.currentProject.id : assetID ?? this._projectService.currentProject.id
                                    const fileContext = publicProject ? 'public' : assetID != null ? 'asset' : undefined

                                    return this._fileReferenceService.getUrlForFileReference(attachment.fileReference, fileOwnerID, fileContext).pipe(
                                        switchMap(url => {
                                            return this._fileReferenceService.createBlob(url).pipe(
                                                map(blob => `<p></p><img src="${blob}"></img>`)
                                            )
                                        })
                                    )
                                }
                            }
                        } else {
                            const { displayName, url, headers, showInDescription } = JSON.parse(attachment.value)

                            if (showInDescription) {
                                const formattedHeaders = {}

                                headers.forEach(header => formattedHeaders[header['key']] = header['value'])

                                return this._fileReferenceService.createBlob(url, formattedHeaders).pipe(
                                    map(blob => `<p></p><h2>${displayName}</h2><img src="${blob}"></img>`)
                                )
                            }
                        }

                        return null
                    })
                    .filter(blobs => blobs !== null)

                if (embeddedImageSources.length > 0) {
                    forkJoin(embeddedImageSources).subscribe(blobs => {
                        blobs.forEach((blob) => description += blob)

                        this._featureService.updateFeaturesLocally(feature)
                        this._featureService.retrievedDetails.add(feature.id)
                        this.setDescription(description)
                    })
                } else {
                    this._featureService.retrievedDetails.add(feature.id)
                }
            })
        )

        // Refresh attachments when properties change on the same feature (e.g. adding/removing attachments)
        // The main subscription above uses distinctUntilKeyChanged('id') which only fires on feature switch.
        this._subscriptions.push(
            this._featureService.selectedFeature$.pipe(
                filter(feature => feature != null && this.feature?.id === feature.id),
                map(feature => new Feature(feature.sceneID, feature.name, feature.type, feature)),
            ).subscribe(feature => {
                this.feature = feature
                this.attachments = feature?.attachments
                    ?.filter(attachment => {
                        if (attachment.value == null || attachment.value == '') return false
                        else if (attachment.isStoredFile) return true
                        else {
                            try {
                                return JSON.parse(attachment.value)?.url != null && JSON.parse(attachment.value)?.url != ''
                            } catch (e) { return false }
                        }
                    })
                    ?.sort((a, b) => {
                        const getValue = (p: FeatureProperty) => {
                            if (p.isStoredFile) return (p.s3ObjectReference?.filename ?? p.value)?.toString().toLowerCase() || ''
                            try { return JSON.parse(p.value)?.displayName?.toString().toLowerCase() || '' }
                            catch (e) { return '' }
                        }
                        return getValue(a) > getValue(b) ? 1 : getValue(a) < getValue(b) ? -1 : 0
                    }) ?? []
            })
        )
    }

    private isStoredImageAttachment(attachment: FeatureProperty): boolean {
        if (!attachment?.isStoredFile) return false

        const filename = attachment.displayFilename?.toLowerCase() ?? ''
        return ['.png', '.jpg', '.jpeg', '.gif', '.webp'].some(extension => filename.endsWith(extension))
    }

    private clearState() {
        // Clear all state variables to prevent old data from showing
        this.feature = null
        this.attachments = []
        this.customFields = []
        this.isDescriptionSet = false
        this.retrievingCustomFields = false
        this.sanitizedMessage = null

        // Force change detection to clear any rendered content
        setTimeout(() => {
            if (this.detailsType == 'modal') {
                this._rightSidebarService.prepareHTML("modal-details")
            } else if (this.detailsType == 'panel') {
                this._rightSidebarService.prepareHTML("right-sidepanel")
            }
        }, 0)
    }

    private setDescription(text: string) {
        if (text == null || text == '') {
            this.isDescriptionSet = false
        } else {
            this.isDescriptionSet = true

            setTimeout(() => {
                const displayText = this._rightSidebarService.rewriteHostedProjectImageUrls(text, this.feature?.embeddedImages ?? [])
                this.sanitizedMessage = this._domSanitizer.bypassSecurityTrustHtml(displayText)

                if (this.detailsType == 'modal') {
                    this._rightSidebarService.prepareHTML("modal-details")
                } else if (this.detailsType == 'panel') {
                    this._rightSidebarService.prepareHTML("right-sidepanel")
                }
            })
        }
    }

    close() {
        this.modal?.hide()
    }

    ngOnDestroy() {
        this._subscriptions.forEach(subscription => subscription?.unsubscribe())
    }
}
